import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import LogotypeDashboardImage from "../../assets/logotype_dashboard.svg";
import { NavbarSearch } from "../../components/navbarSearch/navbarSearch";
import { IndicatorsContainer } from "../performanceIndicators/indicatorsContainer";
import { useStore } from "../../models";
import { Path } from "../../models/router";
import { AgentCallSection } from "../call/agentCallSection";
import styles from "./header.css";
import { Status } from "../../components/status/Status";
import { DashboardView } from "./views/DashboardView";
import { AppPermission } from "@closerplatform/spinner-openapi";
import { CloserLogo } from "@fidget/common/components/image/closerLogo/closerLogo";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import { HeaderTabOption, HeaderTabs } from "./HeaderTabs/HeaderTabs";
import { isAITabActive, isReportsTabActive } from "./HeaderTabs/utils";
import { compact } from "lodash";
import { useWindowDimensions, WindowSize } from "../../models/WindowDimensions";

interface Props {
  view: DashboardView;
}

export const AgentDashboardHeader = observer(({ view }: Props) => {
  const {
    callStore,
    inboxStore,
    roomStore,
    currentUserStore,
    uiStore,
    cobrowsingStore,
    reportsStore
  } = useStore();
  const landingReportTab = reportsStore.landingTab;
  const roomId = roomStore.currentRoom && roomStore.currentRoom.id;
  const { windowSize } = useWindowDimensions();
  const settingsView =
    view !== DashboardView.CALENDAR &&
    view !== DashboardView.CONVERSATIONS &&
    !isReportsTabActive(view) &&
    !isAITabActive(view);

  const renderCloserLogo = () => {
    switch (windowSize) {
      case WindowSize.Small: {
        return (
          <div className={styles["closer-icon-container"]}>
            <CloserLogo className={styles["closer-icon"]} />
          </div>
        );
      }
      case WindowSize.Default: {
        return (
          <img
            className={styles["closer-logo"]}
            alt={"CLOSER Logotype"}
            src={LogotypeDashboardImage}
          />
        );
      }
      default: {
        assertUnreachable(windowSize);
        return undefined;
      }
    }
  };

  const getHeaderTabsOptions = (): HeaderTabOption[] => {
    const conversationsTab: HeaderTabOption = {
      to: roomId ? Path.room(roomId) : Path.MAIN,
      id: "menu_dashboard",
      name: intl.formatMessage("header.tab.conversations"),
      isEnabled: true,
      isActive: view === DashboardView.CONVERSATIONS
    };

    const meetingsTab: HeaderTabOption = {
      to: Path.CALENDAR,
      id: "menu_calendar",
      name: intl.formatMessage("header.tab.calendar"),
      isEnabled: uiStore.canDisplayOnlineMeetings(),
      isActive: view === DashboardView.CALENDAR
    };

    const aiSugestionsTab: HeaderTabOption = {
      to: Path.AI_SETTINGS_GENERAL,
      id: "menu_ai",
      name: intl.formatMessage("header.tab.ai"),
      isEnabled: !!uiStore.canDisplayWithPermission(AppPermission.SettingsAiSuggestions),
      isActive: isAITabActive(view)
    };

    const reportsTab: HeaderTabOption | undefined = landingReportTab && {
      to: Path.reports(landingReportTab.view, landingReportTab.scope),
      id: "menu_reports",
      name: intl.formatMessage("header.tab.reports"),
      isEnabled: !!(uiStore.canDisplayReports() && currentUserStore.orgConfig?.reportsEnabled),
      isActive: isReportsTabActive(view)
    };

    const headerTabsOptions: HeaderTabOption[] = compact([
      conversationsTab,
      meetingsTab,
      aiSugestionsTab,
      reportsTab
    ]);
    return headerTabsOptions;
  };

  return (
    <div className={styles.header}>
      {(callStore.state || cobrowsingStore.cobrowsingAccepted) && <AgentCallSection />}
      <div className={styles["menu-section"]}>
        {renderCloserLogo()}
        <HeaderTabs tabs={getHeaderTabsOptions()} />
        <NavbarSearch searchResultPicked={inboxStore.selectSearchResult} />
      </div>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <IndicatorsContainer />
        {uiStore.canDisplayGuides() && (
          <a
            className={classNames(styles["menu-option"], styles.guides)}
            href={"https://support.closer.app/"}
            target={"_blank"}
          >
            <i className={"icon-compass"} />
          </a>
        )}
        <Status settingsView={settingsView} />
      </div>
    </div>
  );
});
